/** The `account` area: the account page (/account) and its contacts screen
 *  (/account/contacts). Owned by the stage 2 slot people (docs/I18N.md §11).
 *  The keys read in the page's order, top to bottom. */
export const account = {
  // --- Page frame and the four group headings (page.tsx; DESIGN_SYSTEM.md
  //     "The person and the instance" rule 4: one noun per group) ---
  'account.page.title': 'Account',
  'account.profile.title': 'Profile',
  'account.signIn.title': 'Sign-in',
  'account.reach.title': 'Reach',
  'account.agents.title': 'Agents',
  'account.storage.title': 'Storage',
  /** The Storage group's one row (docs/ATTACHMENTS.md §4): the person's files
   *  still alive against the per-person limit, then today's files against the
   *  daily limit. Every value is a `common.bytes` ("12 MB"). */
  'account.storage.files': 'Files',
  'account.storage.used': '{alive} of {limit}',
  'account.storage.today': '{today} today of {dayLimit}',
  /** The (?) beside a group heading: its accessible name. */
  'account.group.help': 'About {group}',
  /** The one sentence behind each heading's (?). */
  'account.signIn.help':
    'A provider you connect here can sign you in as you. A provider account that merely carries your address never reaches this one.',
  'account.reach.help':
    'People who share no work with you reach you as a contact; others must offer a seat, which you accept, unless you allow them here.',
  'account.agents.help':
    'An agent acts with your authority, narrowed to the level and the reach you chose for it.',

  // --- Profile: the name (account-forms.tsx) ---
  'account.profile.name': 'Name',
  /** The name field's accessible name while the row is open. */
  'account.profile.nameField': 'Display name',
  'account.profile.edit': 'Change name',
  'account.profile.save': 'Save',
  'account.profile.cancel': 'Cancel',
  /** When the save fails and the sign-in service gives no reason. */
  'account.profile.saveFailed': 'Could not save.',

  // --- Profile: the handle (handle-form.tsx) ---
  'account.handle.label': 'Handle',
  'account.handle.none': 'none yet',
  /** The caret's name: a person with a handle changes it, one without picks one. */
  'account.handle.change': 'Change handle',
  'account.handle.pick': 'Pick a handle',
  /** Beside the handle while the cooldown runs: when it may change again. */
  'account.handle.until': 'until {date}',
  'account.handle.opensAt': 'It can change again on {date}.',
  'account.handle.field': 'Handle',
  'account.handle.hint':
    '5 to 24 letters, digits or underscore. You can change it once every {days} days.',
  'account.handle.save': 'Save',
  'account.handle.cancel': 'Cancel',
  'account.handle.remove': 'Remove handle',
  'account.handle.removeQuestion':
    'Remove it? Nobody else can take it, and you can pick again after {days} days.',
  'account.handle.removeConfirm': 'Remove',
  'account.handle.keep': 'Keep',
  /** What is wrong with a typed handle (domain/handle.ts `handleShapeError`). */
  'account.handle.shape.length': 'A handle has {min} to {max} characters.',
  'account.handle.shape.letter': 'A handle starts with a letter.',
  'account.handle.shape.chars': 'A handle uses letters a-z, digits and underscore only.',
  /** A handle change refused (domain/handle.ts `decideHandleChange`). */
  'account.handle.error.noneToClear': 'You have no handle to clear.',
  'account.handle.error.same': 'That is your handle already.',
  'account.handle.error.cooldown': 'Your handle can change again on {date}.',
  /** One sentence for taken, parked and reserved alike: it reveals nothing. */
  'account.handle.error.notAvailable': 'That handle is not available.',
  'account.handle.error.personOnly': 'Only a person has a handle.',
  'account.handle.error.rateLimited':
    'Too many handle changes tried recently. Please wait a while.',

  // --- Profile: the address (account-forms.tsx) ---
  'account.email.label': 'Address',
  'account.email.verified': 'verified',
  'account.email.change': 'Change',
  /** The act on an address that is not verified yet: mails a new code and
   *  opens the verification page. */
  'account.email.verify': 'Verify',
  'account.email.verifyFailed': 'Could not send a code.',
  'account.email.dialogTitle': 'Change address',
  'account.email.newLabel': 'New address',
  'account.email.newHint':
    'You approve the move from your current address, then confirm the new one.',
  /** The code field's label: which mailbox the code was sent to. */
  'account.email.codeFrom': 'Code from {email}',
  'account.email.continue': 'Continue',
  'account.email.approve': 'Approve',
  'account.email.confirm': 'Change address',
  'account.email.approvalSent': 'Check {email} to approve the change.',
  'account.email.verifySent': 'Check {email} to verify it.',
  /** When a code is refused and the sign-in service gives no reason. */
  'account.email.codeFailed': 'That code did not work.',
  'account.email.changed': 'Address changed.',

  // --- Profile: the language (locale-section.tsx) ---
  'account.locale.title': 'Language',
  'account.locale.edit': 'Change language',
  'account.locale.cancel': 'Cancel',
  'account.locale.browser': 'Browser default',
  'account.locale.saveFailed': 'Could not save.',

  // --- Sign-in: the password (account-forms.tsx) ---
  'account.password.label': 'Password',
  'account.password.none': 'not set',
  /** Beside the dots: when the password was set. */
  'account.password.setOn': 'set {date}',
  'account.password.changeAct': 'Change',
  'account.password.setAct': 'Set',
  'account.password.changeTitle': 'Change password',
  'account.password.setTitle': 'Set a password',
  /** The Set dialog's subtitle, for a person who has no password yet. */
  'account.password.setHint': 'A password is your own way in, next to any provider you connect.',
  'account.password.current': 'Current password',
  'account.password.new': 'New password',
  'account.password.minLength': 'At least 8 characters.',
  'account.password.confirm': 'Confirm new password',
  'account.password.mismatch': 'Does not match.',
  'account.password.setButton': 'Set password',
  'account.password.set': 'Password set. You can now sign in with your email and password too.',
  'account.password.changeButton': 'Change password',
  'account.password.changed': 'Password changed. Other sessions were signed out.',
  /** When the change fails and the sign-in service gives no reason. */
  'account.password.changeFailed': 'Could not change.',
  /** better-auth refused to set a first password and said nothing more. */
  'account.password.setFailed': 'Could not set a password.',

  // --- Sign-in providers (connected-identities.tsx) ---
  /** The ghost row that opens the menu of providers not yet linked. */
  'account.providers.add': 'Connect a provider',
  'account.providers.disconnect': 'Disconnect',
  /** Beside the only identity left, whose Disconnect is disabled. */
  'account.providers.onlyWayIn': 'your only way in',
  'account.providers.connectFailed': 'Could not connect.',
  'account.providers.disconnectFailed': 'Could not disconnect.',
  /** A refused round trip, when the identity already belongs to another account. */
  'account.providers.alreadyLinked': 'That identity is already connected to another account here.',
  /** Any other refused round trip. */
  'account.providers.roundTripFailed': 'Could not connect that provider. Try again.',
  /** The confirmation after a connect: its title, its two lines and its answers. */
  'account.providers.connectedTitle': '{provider} connected',
  'account.providers.providerFallback': 'Provider',
  'account.providers.accepts': 'This account now accepts sign-ins from {provider} as {identity}',
  'account.providers.notYou': 'If that is not you, disconnect it now.',
  'account.providers.keep': 'Keep this sign-in',
  /** What an identity is called when the provider sent no address and no name. */
  'account.providers.identityFallback': 'connected',
  'account.providers.identityJustChosen': 'the account you just chose',

  // --- Reach: contacts (reach-rows.tsx, contacts/page.tsx) ---
  'account.contacts.title': 'Contacts',
  'account.contacts.none': 'none yet',
  /** After the first names shown: how many more contacts there are. */
  'account.contacts.more': '+{count}',
  /** Requests and group invitations that wait for an answer. */
  'account.contacts.waiting': '{count} waiting',
  'account.contacts.open': 'Open contacts',

  // --- Reach: who may seat me directly (reach-rows.tsx) ---
  'account.seat.label': 'Can seat me',
  /** The rule in words: an explicit circle names its people. */
  'account.seat.only': 'Only {names}',
  'account.seat.nobody': 'Nobody directly',
  'account.seat.edit': 'Change who can seat me',
  'account.seat.dialogTitle': 'Who can seat me directly',
  'account.seat.dialogHint':
    'Others must offer a seat, which you accept, unless you allow them here.',
  'account.seat.done': 'Done',

  // --- Reach: notifications on this device (push-section.tsx, actions.ts) ---
  'account.push.label': 'This device',
  'account.push.on': 'Notifications on',
  'account.push.off': 'Notifications off',
  /** Every device of the person that receives notifications, this one included. */
  'account.push.devicesOn': { one: '{count} device on', other: '{count} devices on' },
  /** Why this device cannot be paged, one sentence per reason. */
  'account.push.unsupported': 'Not available in this browser.',
  'account.push.needsInstall':
    'Add Tesserano to your Home Screen first, then turn notifications on from the installed app.',
  'account.push.blocked': "Notifications are blocked for Tesserano in this browser's settings.",
  'account.push.turnOn': 'Turn on',
  'account.push.turnOff': 'Turn off',
  'account.push.badSubscription': 'This browser returned a subscription the server cannot use.',
  'account.push.turnOnFailed': 'Could not turn notifications on for this device.',
  'account.push.turnOffFailed': 'Could not turn notifications off for this device.',
  /** The test notification's text, sent to every device of the person. */
  'account.push.testBody': 'Notifications are on for this device.',

  // --- Agents: one list of every agent grant, connector and key alike
  //     (agents-section.tsx, docs/AGENT_ACCESS.md §6) ---
  /** The dim line under a grant's name: its lane and since when. */
  'account.agents.connectorSince': 'connector · since {date}',
  'account.agents.keySince': 'key · since {date}',
  /** Where a grant acts, in the row's short form. `groups`: the groups' names. */
  'account.agents.reach.groups': '{groups}',
  'account.agents.reach.groupsOwn': '{groups} and your own items',
  'account.agents.open': 'Details of {name}',
  /** The details dialog's rows. */
  'account.agents.detail.lane': 'Type',
  'account.agents.detail.connector': 'Connector',
  'account.agents.detail.key': 'Key',
  'account.agents.detail.label': 'Label',
  'account.agents.detail.actsAs': 'Acts as',
  'account.agents.detail.may': 'May',
  'account.agents.detail.where': 'Where',
  'account.agents.detail.since': 'Since',
  'account.agents.revoke': 'Revoke',
  /** The ghost row and its dialog, which offers the two lanes. */
  'account.agents.add': 'Add an agent',
  'account.agents.lanes': 'How the agent connects',
  'account.agents.lane.claude': 'Connect Claude',
  'account.agents.lane.key': 'Mint a key',

  // --- Connect Claude (connector-section.tsx, docs/AGENT_ACCESS.md §6a).
  //     The steps name the Claude app's own menus, as Claude shows them in
  //     English. ---
  'account.claude.hint':
    'Claude can find, read and update your items when you ask it to. Add Tesserano to Claude once with this address.',
  'account.claude.addressLabel': 'Address to paste into Claude',
  'account.claude.copy': 'Copy',
  'account.claude.copied': 'Copied.',
  'account.claude.selected': 'The address is selected. Copy it with your device’s copy command.',
  'account.claude.step1': 'In Claude, open Customize, then Connectors.',
  'account.claude.step2':
    'Choose Add custom connector. Name it Tesserano, paste the address, and choose Add.',
  'account.claude.step3': 'Choose Connect. Sign in to Tesserano if asked, then choose Allow.',

  // --- Delete account (deletion-section.tsx) ---
  'account.deletion.title': 'Delete account',
  'account.deletion.hint':
    'Signs you out everywhere at once. Your personal data is removed after {days} days.',
  'account.deletion.open': 'Delete…',
  /** The confirmation dialog: what ends now, what is removed after the grace
   *  period, and who can restore the account until then. */
  'account.deletion.dialogTitle': 'Delete your account',
  'account.deletion.now':
    'Now: you are signed out on every device, your agent keys stop working, your devices stop receiving notifications, and nobody can add you to new work. Your name stays on the work you took part in.',
  'account.deletion.after':
    'After {days} days: your name, address, sign-in methods, contacts and settings are removed. Your name on that work then reads “Deleted account”. The work itself stays.',
  'account.deletion.restore':
    'Until then an operator of this instance can restore the account. You cannot undo it yourself.',
  'account.deletion.typeToConfirm': 'Type your address to confirm',
  'account.deletion.cancel': 'Cancel',
  'account.deletion.confirm': 'Delete account',
} as const;
